// src/components/garages/GarageList.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  fetchGarages,
  deleteGarage,
  clearGarageError,
  clearGarageSuccess,
} from '../../store/actions/garageActions';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import { GARAGE_TYPES, APPROVAL_STATUS } from '../../store/actions/actionTypes';

const GarageList = () => {
  const dispatch = useDispatch();
  const { garages, loading, error, success, pagination } = useSelector(
    (state) => state.garages
  );
  
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    search: '',
    type: '',
    city: '',
    approvalStatus: '',
    minRating: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [selectedGarage, setSelectedGarage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  const initialMount = useRef(true);
  const filtersRef = useRef(filters);

  const styles = {
    container: {
      maxWidth: '1440px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    createButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    createButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    filterToggle: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    filterToggleHover: {
      backgroundColor: '#e5e7eb',
    },
    filterPanel: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      border: '1px solid #e5e7eb',
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginBottom: '1rem',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    filterLabel: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    filterSelect: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      backgroundColor: '#ffffff',
    },
    filterInput: {
      padding: '0.5rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
    },
    filterActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '1rem',
    },
    clearButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    clearButtonHover: {
      backgroundColor: '#f3f4f6',
    },
    applyButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    searchContainer: {
      marginBottom: '1rem',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
      borderBottom: '1px solid #e5e7eb',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#f9fafb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    garageName: {
      fontWeight: 600,
      color: '#111827',
    },
    garageSiret: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    garageAddress: {
      fontSize: '0.875rem',
      color: '#4b5563',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    typeBadge: {
      backgroundColor: '#e0e7ff',
      color: '#3730a3',
    },
    ratingContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    ratingStars: {
      display: 'flex',
      gap: '0.125rem',
    },
    starFilled: {
      color: '#fbbf24',
      width: '1rem',
      height: '1rem',
    },
    starEmpty: {
      color: '#d1d5db',
      width: '1rem',
      height: '1rem',
    },
    ratingValue: {
      fontSize: '0.875rem',
      color: '#4b5563',
      marginLeft: '0.25rem',
    },
    statusBadge: (status) => ({
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
      backgroundColor: 
        status === 'APPROVED' ? '#d1fae5' :
        status === 'PENDING' ? '#fef3c7' :
        status === 'REJECTED' ? '#fee2e2' : '#f3f4f6',
      color: 
        status === 'APPROVED' ? '#065f46' :
        status === 'PENDING' ? '#92400e' :
        status === 'REJECTED' ? '#991b1b' : '#374151',
    }),
    servicesContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.25rem',
    },
    serviceBadge: {
      padding: '0.125rem 0.375rem',
      fontSize: '0.75rem',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      borderRadius: '0.25rem',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    actionButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    viewButton: {
      color: '#2563eb',
    },
    viewButtonHover: {
      color: '#1d4ed8',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    approveButton: {
      color: '#059669',
    },
    approveButtonHover: {
      color: '#047857',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  const fetchGaragesData = useCallback(() => {
    dispatch(fetchGarages(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      fetchGaragesData();
    } else {
      if (JSON.stringify(filtersRef.current) !== JSON.stringify(filters)) {
        filtersRef.current = filters;
        fetchGaragesData();
      }
    }
  }, [fetchGaragesData, filters]);

  useEffect(() => {
    return () => {
      dispatch(clearGarageError());
      dispatch(clearGarageSuccess());
    };
  }, [dispatch]);

  const handleSearch = useCallback((searchTerm) => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1,
    }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      search: '',
      type: '',
      city: '',
      approvalStatus: '',
      minRating: '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
    });
  };

  const handleDeleteClick = useCallback((garage) => {
    setSelectedGarage(garage);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await dispatch(deleteGarage(selectedGarage.id));
      setShowDeleteConfirm(false);
      setSelectedGarage(null);
      fetchGaragesData();
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, selectedGarage, fetchGaragesData]);

  const handleCloseDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setSelectedGarage(null);
  }, []);

  const handleClearError = useCallback(() => {
    dispatch(clearGarageError());
  }, [dispatch]);

  const handleClearSuccess = useCallback(() => {
    dispatch(clearGarageSuccess());
  }, [dispatch]);

  const getStatusLabel = (status) => {
    return status.charAt(0) + status.slice(1).toLowerCase();
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= Math.floor(rating)) {
        stars.push(
          <svg key={i} style={styles.starFilled} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      } else {
        stars.push(
          <svg key={i} style={styles.starEmpty} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        );
      }
    }
    return stars;
  };

  if (loading && !garages.length) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <h1 style={styles.title}>Gestion des garages</h1>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={styles.filterToggle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.filterToggleHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.filterToggle.backgroundColor;
            }}
          >
            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {showFilters ? 'Cacher les filtres' : 'Afficher les filtres'}
          </button>
        </div>
        <Link
          to="/admin/garages/form"
          style={styles.createButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.createButton.backgroundColor;
          }}
        >
          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter un nouveau garage
        </Link>
      </div>

      {showFilters && (
        <div style={styles.filterPanel}>
          <div style={styles.filterGrid}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>City</label>
              <input
                type="text"
                name="city"
                value={filters.city}
                onChange={handleFilterChange}
                placeholder="Filtrer par ville"
                style={styles.filterInput}
              />
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Type de garage</label>
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                style={styles.filterSelect}
              >
                <option value="">Tout Type</option>
                {Object.entries(GARAGE_TYPES).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value.charAt(0) + value.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Statut d'approbation</label>
              <select
                name="approvalStatus"
                value={filters.approvalStatus}
                onChange={handleFilterChange}
                style={styles.filterSelect}
              >
                <option value="">Tout Statut</option>
                {Object.entries(APPROVAL_STATUS).map(([key, value]) => (
                  <option key={key} value={value}>
                    {value.charAt(0) + value.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Note minimale</label>
              <select
                name="minRating"
                value={filters.minRating}
                onChange={handleFilterChange}
                style={styles.filterSelect}
              >
                <option value="">Toute Note</option>
                <option value="1">1+ Etoile</option>
                <option value="2">2+ Etoiles</option>
                <option value="3">3+ Etoiles</option>
                <option value="4">4+ Etoiles</option>
                <option value="5">5 Etoiles</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Trier par</label>
              <select
                name="sortBy"
                value={filters.sortBy}
                onChange={handleFilterChange}
                style={styles.filterSelect}
              >
                <option value="createdAt">Date d'ajout</option>
                <option value="name">Nom</option>
                <option value="rating">Note</option>
                <option value="totalRepairs">Total Réparations</option>
              </select>
            </div>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Ordre de tri</label>
              <select
                name="sortOrder"
                value={filters.sortOrder}
                onChange={handleFilterChange}
                style={styles.filterSelect}
              >
                <option value="desc">Descendant</option>
                <option value="asc">Ascendant</option>
              </select>
            </div>
          </div>
          <div style={styles.filterActions}>
            <button
              onClick={handleClearFilters}
              style={styles.clearButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = styles.clearButtonHover.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = styles.clearButton.backgroundColor;
              }}
            >
              Annuler les filtres
            </button>
          </div>
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={handleClearError}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={handleClearSuccess}
        />
      )}

      <div style={styles.searchContainer}>
        <SearchBar
          onSearch={handleSearch}
          placeholder="Rechercher des garages par nom, SIRET ou ville..."
          fullWidth
          initialValue={filters.search}
        />
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Garage</th>
              <th style={styles.tableHeaderCell}>Type</th>
              <th style={styles.tableHeaderCell}>Location</th>
              <th style={styles.tableHeaderCell}>Services</th>
              <th style={styles.tableHeaderCell}>Note</th>
              <th style={styles.tableHeaderCell}>Status</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {garages.map((garage) => (
              <tr
                key={garage.id}
                style={styles.tableRow}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <td style={styles.tableCell}>
                  <div style={styles.garageName}>{garage.name}</div>
                  <div style={styles.garageSiret}>SIRET: {garage.siret}</div>
                </td>
                <td style={styles.tableCell}>
                  <span style={{ ...styles.badge, ...styles.typeBadge }}>
                    {garage.type}
                  </span>
                </td>
                <td style={styles.tableCell}>
                  {garage.address && (
                    <div style={styles.garageAddress}>
                      {garage.address.city}, {garage.address.postalCode}
                    </div>
                  )}
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.servicesContainer}>
                    {garage.services?.slice(0, 3).map((service) => (
                      <span key={service.id} style={styles.serviceBadge}>
                        {service.type}
                      </span>
                    ))}
                    {garage.services?.length > 3 && (
                      <span style={styles.serviceBadge}>
                        +{garage.services.length - 3}
                      </span>
                    )}
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <div style={styles.ratingContainer}>
                    <div style={styles.ratingStars}>
                      {renderStars(garage.rating || 0)}
                    </div>
                    <span style={styles.ratingValue}>
                      {garage.rating ? garage.rating.toFixed(1) : 'N/A'}
                    </span>
                  </div>
                </td>
                <td style={styles.tableCell}>
                  <span style={styles.statusBadge(garage.approvalStatus)}>
                    {getStatusLabel(garage.approvalStatus)}
                  </span>
                </td>
                <td style={styles.tableCellRight}>
                  <div style={styles.actionsContainer}>
                    <Link
                      to={`/admin/garages/${garage.id}/details`}
                      style={{
                        ...styles.actionButton,
                        ...styles.viewButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.viewButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.viewButton.color;
                      }}
                      title="View Details"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </Link>

                    <Link
                      to={`/admin/garages/${garage.id}/edit`}
                      style={{
                        ...styles.actionButton,
                        ...styles.editButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.editButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.editButton.color;
                      }}
                      title="Edit Garage"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>

                    {garage.approvalStatus === 'PENDING' && (
                      <button
                        onClick={() => {/* Handle approval */}}
                        style={{
                          ...styles.actionButton,
                          ...styles.approveButton,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = styles.approveButtonHover.color;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = styles.approveButton.color;
                        }}
                        title="Review Approval"
                      >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    )}

                    <button
                      onClick={() => handleDeleteClick(garage)}
                      style={{
                        ...styles.actionButton,
                        ...styles.deleteButton,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = styles.deleteButtonHover.color;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = styles.deleteButton.color;
                      }}
                      title="Delete Garage"
                    >
                      <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {garages.length === 0 && !loading && (
          <div style={styles.emptyState}>
            Aucun garage trouvé. Essayez d'ajuster vos filtres ou votre recherche.
          </div>
        )}

        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le garage"
        message={`Êtes-vous sûr de vouloir supprimer "${selectedGarage?.name}" ? Cette action ne peut pas être annulée.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default GarageList;